import { useMemo } from "react";
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, ComposedChart } from "recharts";
import { calculateMetricsFromTasks } from "@/lib/utils";
import { chart, scoreColor } from "@/lib/brand";
import { ChartEmpty, ChartHeader, axisLabelStyle, axisTick } from "@/components/charts/ChartShell";
import type { Metrics, Task } from "@/types/dashboard";

interface AccuracyTrendProps {
  metrics: Metrics | null;
  tasks?: Record<string, Task>;
  totalQuestions?: number;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="chart-tooltip">
        <p className="font-semibold text-foreground">Vraag {data.index}</p>
        <p className="text-muted-foreground">
          Score: <span className="font-semibold tabular-nums text-foreground">{data.score?.toFixed(1) ?? '–'}</span>
        </p>
        <p className="text-muted-foreground">
          Gemiddelde: <span className="font-semibold tabular-nums text-foreground">{data.runningAvg?.toFixed(2) ?? '0.00'}</span> / 5
        </p>
      </div>
    );
  }
  return null;
};

const TITLE = "Nauwkeurigheid per vraag";

export function AccuracyTrend({ metrics: propMetrics, tasks, totalQuestions }: AccuracyTrendProps) {
  // Calculate metrics from tasks (single source of truth)
  const metrics = useMemo(() => {
    if (tasks && Object.keys(tasks).length > 0) {
      return calculateMetricsFromTasks(tasks, totalQuestions);
    }
    return propMetrics;
  }, [tasks, totalQuestions, propMetrics]);

  const chartData = useMemo(() => {
    if (!metrics || !metrics.scores || metrics.scores.length === 0) return [];

    const data: { index: number; score: number; runningAvg: number }[] = [];
    let sum = 0;

    metrics.scores.forEach((score, index) => {
      sum += score;
      const runningAvg = sum / (index + 1);
      data.push({
        index: index + 1,
        score,
        runningAvg,
      });
    });

    return data;
  }, [metrics]);

  if (!metrics || chartData.length === 0) {
    return <ChartEmpty title={TITLE} />;
  }

  return (
    <div className="flex h-full w-full flex-col">
      <ChartHeader
        title={TITLE}
        subtitle={`Lopend gemiddelde over ${metrics.scores.length} beoordeelde ${metrics.scores.length === 1 ? 'vraag' : 'vragen'}`}
        aside={
          <>
            <div>
              <span className="font-display text-xl font-semibold tabular-nums" style={{ color: scoreColor(metrics.mean_accuracy || 1) }}>
                {metrics.mean_accuracy?.toFixed(2) ?? '0.00'}
              </span>
              <span className="text-muted-foreground"> / 5</span>
            </div>
            {metrics.failed > 0 && (
              <div className="font-medium text-destructive">
                {metrics.failed} {metrics.failed === 1 ? 'fout' : 'fouten'} (niet in gemiddelde)
              </div>
            )}
          </>
        }
      />

      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 20 }}>
            <defs>
              <linearGradient id="accuracyAreaFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={chart.areaFill} stopOpacity={0.35} />
                <stop offset="95%" stopColor={chart.areaFill} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
            <XAxis
              dataKey="index"
              tick={axisTick}
              stroke={chart.axis}
              tickLine={false}
              label={{ value: 'Vraag', position: 'insideBottom', offset: -15, style: axisLabelStyle }}
            />
            <YAxis
              domain={[0, 5]}
              ticks={[0, 1, 2, 3, 4, 5]}
              tick={axisTick}
              stroke={chart.axis}
              tickLine={false}
              width={44}
              label={{ value: 'Score', angle: -90, position: 'insideLeft', style: axisLabelStyle }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: chart.axis, strokeWidth: 1, strokeDasharray: '3 3' }} />
            <ReferenceLine
              y={metrics.mean_accuracy}
              stroke={chart.accent}
              strokeDasharray="5 5"
              strokeWidth={1.5}
              label={{
                value: `Gem. ${metrics.mean_accuracy?.toFixed(2) ?? '0.00'}`,
                position: 'insideTopRight',
                fill: chart.accent,
                fontSize: 11,
                fontWeight: 600,
              }}
            />
            <Area type="monotone" dataKey="runningAvg" fill="url(#accuracyAreaFill)" stroke="none" />
            <Line
              type="monotone"
              dataKey="runningAvg"
              stroke={chart.primary}
              strokeWidth={2}
              dot={{ fill: chart.primary, r: 2.5, strokeWidth: 0 }}
              activeDot={{ r: 5, fill: chart.areaFill, stroke: chart.primary, strokeWidth: 2 }}
              name="Lopend gemiddelde"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
